import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  Spinner,
} from '@makeably/creativex-design-system';
import ActivatedMedia from 'components/creative_lifecycle/ActivatedMedia';
import Usage from 'components/creative_lifecycle/Usage';
import DsTabs from 'components/molecules/DsTabs';
import { getOptions } from 'components/molecules/FilterButton';
import FilterWithTags from 'components/molecules/FilterWithTags';
import { addToast } from 'components/organisms/Toasts';
import {
  attributeLookupsProps,
  dateOptionProps,
} from 'components/reporting/shared';
import { arrayIf } from 'utilities/array';
import { track } from 'utilities/mixpanel';
import { removeProperty } from 'utilities/object';
import { get } from 'utilities/requests';
import {
  activatedMediaRecordsCreativeLifecycleDashboardIndexPath,
  usageRecordsCreativeLifecycleDashboardIndexPath,
} from 'utilities/routes';
import { titleize } from 'utilities/string';

const USAGE_TAB = 'Usage';
const ACTIVATED_MEDIA_TAB = 'Activated Media';

const propTypes = {
  canViewSpend: PropTypes.bool.isRequired,
  dateOptions: PropTypes.arrayOf(dateOptionProps).isRequired,
  initialDateOption: dateOptionProps.isRequired,
  lookups: attributeLookupsProps.isRequired,
  qualityRateLabel: PropTypes.string.isRequired,
};

const segments = [
  {
    label: 'Asset Type',
    value: 'assetType',
  },
  {
    label: 'Brand',
    value: 'brand',
  },
  {
    label: 'Campaign Name',
    value: 'campaign',
  },
  {
    label: 'Core\nAsset ID',
    value: 'uuid',
  },
  {
    label: 'Channel',
    value: 'channel',
  },
  {
    label: 'File Name',
    value: 'fileName',
  },
  {
    label: 'Market',
    value: 'market',
  },
  {
    label: 'Measurement Partner',
    value: 'measurementPartner',
  },
  {
    label: 'Production Partner',
    value: 'partner',
  },
];

async function getRecords(dateOption, tab) {
  const params = {
    date_type: dateOption.type,
    start_date: dateOption.startDate,
  };

  switch (tab) {
    case ACTIVATED_MEDIA_TAB:
      return get(activatedMediaRecordsCreativeLifecycleDashboardIndexPath(params));
    case USAGE_TAB:
      return get(usageRecordsCreativeLifecycleDashboardIndexPath(params));
    default:
      throw new Error(`Unknown tab: ${tab}`);
  }
}

function preprocessAssets(assets, lookups) {
  return assets.map((asset) => ({
    ...asset,
    brand: lookups.brand[asset.brandId] || 'N/A',
    channel: lookups.channel[asset.channelId] || 'N/A',
    measurementPartner: asset.measurementPartner || 'N/A',
  }));
}

function getCustomOption(asset, key) {
  const value = asset[key];

  switch (key) {
    case 'uuid':
    case 'fileName':
    case 'measurementPartner': {
      return {
        label: value,
        value,
      };
    }
    default: {
      const label = titleize(value);

      return {
        label,
        value,
      };
    }
  }
}

function getFilteredCoreAssets(assets, filters) {
  let filteredAssets = assets;
  Object.keys(filters).forEach((key) => {
    if (key !== 'channel' && key !== 'market') {
      const keyValues = new Set(filters[key].map(({ value }) => value));
      filteredAssets = filteredAssets.filter((asset) => keyValues.has(asset[key]));
    }
  });
  return filteredAssets;
}

function getFilteredData(data, filters, lookups) {
  let filteredData = preprocessAssets(data, lookups);
  Object.keys(filters).forEach((key) => {
    const keyValues = new Set(filters[key].map(({ value }) => value));
    filteredData = filteredData.filter((asset) => !(key in asset) || keyValues.has(asset[key]));
  });
  return filteredData;
}

function Dashboard({
  qualityRateLabel,
  canViewSpend,
  dateOptions,
  initialDateOption,
  lookups,
}) {
  const lastPulledDates = useRef({
    [ACTIVATED_MEDIA_TAB]: null,
    [USAGE_TAB]: null,
  });
  const [coreAssets, setCoreAssets] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState([]);
  const [totals, setTotals] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(USAGE_TAB);
  const [filters, setFilters] = useState({});
  const [dateFilter, setDateFilter] = useState(initialDateOption);
  const [loading, setLoading] = useState(false);
  const processedCoreAssets = useMemo(
    () => preprocessAssets(coreAssets, lookups),
    [coreAssets],
  );
  const filteredCoreAssets = useMemo(
    () => getFilteredCoreAssets(processedCoreAssets, filters),
    [processedCoreAssets, filters],
  );
  const filterOptions = useMemo(
    () => getOptions(processedCoreAssets, segments, getCustomOption),
    [processedCoreAssets],
  );
  const postFilters = useMemo(
    () => ({
      channels: filters.channel ? filters.channel.map(({ value }) => value) : [],
      markets: filters.market ? filters.market.map(({ value }) => value) : [],
    }),
    [filters],
  );

  useEffect(() => {
    if (lastPulledDates.current[currentTab] === dateFilter.value) return;

    const fetchRecords = async () => {
      setLoading(true);
      try {
        const response = await getRecords(dateFilter, currentTab);

        if (response.isError) {
          const isTimeout = response.status === 504;
          const message = isTimeout ? 'The data request has timed out' : 'The data could not be loaded';
          addToast(message, { type: 'error' });
        } else if (currentTab === ACTIVATED_MEDIA_TAB) {
          setSummaryMetrics(response.data.summaryMetrics || []);
          setTotals(response.data.totals || []);
        } else {
          setCoreAssets(response.data.coreAssets || []);
        }
      } catch (error) {
        addToast('An unexpected error occurred', { type: 'error' });
      } finally {
        lastPulledDates.current[currentTab] = dateFilter.value;
        setLoading(false);
      }
    };

    fetchRecords();
  }, [dateFilter, currentTab]);

  const filteredData = useMemo(
    () => getFilteredData(summaryMetrics, filters, lookups),
    [summaryMetrics, filters],
  );

  const filteredTotals = useMemo(
    () => getFilteredData(totals, filters, lookups), [totals, filters, lookups],
  );

  const handleFilterChange = (selections, keyToRemove = '') => {
    if (keyToRemove) {
      setFilters((last) => removeProperty(last, keyToRemove));
      track('remove_filter_tag', { keyToRemove });
    } else {
      setFilters(selections);
      track('submit_dashboard_filter', { selections });
    }
  };

  const handleDateChange = (date) => {
    setDateFilter(date);

    track('change_date_period', { date });
  };

  const tabs = [
    {
      label: USAGE_TAB,
      onClick: () => { if (!loading) setCurrentTab(USAGE_TAB); },
    },
    ...arrayIf(canViewSpend, {
      label: ACTIVATED_MEDIA_TAB,
      onClick: () => { if (!loading) setCurrentTab(ACTIVATED_MEDIA_TAB); },
    }),
  ];

  return (
    <>
      <DsTabs
        currentTab={currentTab}
        tabs={tabs}
        variant="button"
      />
      { loading && (
        <div className="u-flexColumn u-alignCenter u-gap-16">
          <Spinner />
          <p>Loading data...</p>
        </div>
      ) }
      { !loading && (
        <div className="u-flexColumn u-gap-16">
          <div className="u-flexRow u-justifyBetween">
            <FilterWithTags
              filterOpen={filterOpen}
              filterOptions={filterOptions}
              filters={filters}
              segments={segments}
              onClose={() => setFilterOpen(false)}
              onFilterChange={handleFilterChange}
              onOpen={() => setFilterOpen(true)}
            />
            <Dropdown
              options={dateOptions}
              search={false}
              selected={dateFilter}
              onChange={handleDateChange}
            />
          </div>
          { currentTab === USAGE_TAB && (
            <Usage
              filteredCoreAssets={filteredCoreAssets}
              postFilters={postFilters}
              qualityRateLabel={qualityRateLabel}
            />
          ) }
          { currentTab === ACTIVATED_MEDIA_TAB && (
            <ActivatedMedia
              filteredData={filteredData}
              filteredTotals={filteredTotals}
            />
          ) }
        </div>
      ) }
    </>
  );
}

Dashboard.propTypes = propTypes;

export default Dashboard;
